// Runtime configuration loader
// Supports both build-time env vars (local dev) and runtime config.json (Docker)

let runtimeConfig = null;

// Default values
const defaults = {
    API_SERVER: "https://demo.sonicmedia.io/api/admin/",
    BASE_PATH_BACKEND: "https://demo.sonicmedia.io",
    ENVIRONMENT_NAME: "",
    KEY_GOOGLE_MAPS: "",
    COGNITO_USER_POOL_ID: "",
    COGNITO_APP_CLIENT_ID: "",
    COGNITO_REGION: "ap-southeast-2",
    COGNITO_DOMAIN: ""
};

// Build-time env vars (used in local development)
const buildTimeConfig = {
    API_SERVER: process.env.REACT_APP_BACKEND_SERVER,
    BASE_PATH_BACKEND: process.env.REACT_APP_BASE_PATH_BACKEND,
    ENVIRONMENT_NAME: process.env.REACT_APP_ENVIRONMENT_NAME,
    KEY_GOOGLE_MAPS: process.env.REACT_APP_KEY_GOOGLE_MAPS,
    COGNITO_USER_POOL_ID: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    COGNITO_APP_CLIENT_ID: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
    COGNITO_REGION: process.env.REACT_APP_COGNITO_REGION,
    COGNITO_DOMAIN: process.env.REACT_APP_COGNITO_DOMAIN
};

// Helper to get config value with proper fallback (uses ?? to allow empty strings)
function getConfigValue(runtimeVal, buildTimeVal, defaultVal) {
    if (runtimeVal !== undefined && runtimeVal !== null) return runtimeVal;
    if (buildTimeVal !== undefined && buildTimeVal !== null) return buildTimeVal;
    return defaultVal;
}

// Initialize config - attempts to load runtime config.json, falls back to build-time/defaults
export async function initConfig() {
    if (runtimeConfig) {
        return runtimeConfig;
    }

    try {
        // Cache-busting to prevent stale config after deployments
        const response = await fetch('/config.json?_=' + Date.now(), {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            }
        });

        if (response.ok) {
            const json = await response.json();
            runtimeConfig = {
                basename: '',
                defaultPath: 'manage/dashboard',
                fontFamily: `'OpenSans', sans-serif`,
                borderRadius: 12,
                API_SERVER: getConfigValue(json.API_SERVER, buildTimeConfig.API_SERVER, defaults.API_SERVER),
                BASE_PATH_BACKEND: getConfigValue(json.BASE_PATH_BACKEND, buildTimeConfig.BASE_PATH_BACKEND, defaults.BASE_PATH_BACKEND),
                ENVIRONMENT_NAME: getConfigValue(json.ENVIRONMENT_NAME, buildTimeConfig.ENVIRONMENT_NAME, defaults.ENVIRONMENT_NAME),
                KEY_GOOGLE_MAPS: getConfigValue(json.KEY_GOOGLE_MAPS, buildTimeConfig.KEY_GOOGLE_MAPS, defaults.KEY_GOOGLE_MAPS),
                COGNITO_USER_POOL_ID: getConfigValue(json.COGNITO_USER_POOL_ID, buildTimeConfig.COGNITO_USER_POOL_ID, defaults.COGNITO_USER_POOL_ID),
                COGNITO_APP_CLIENT_ID: getConfigValue(json.COGNITO_APP_CLIENT_ID, buildTimeConfig.COGNITO_APP_CLIENT_ID, defaults.COGNITO_APP_CLIENT_ID),
                COGNITO_REGION: getConfigValue(json.COGNITO_REGION, buildTimeConfig.COGNITO_REGION, defaults.COGNITO_REGION),
                COGNITO_DOMAIN: getConfigValue(json.COGNITO_DOMAIN, buildTimeConfig.COGNITO_DOMAIN, defaults.COGNITO_DOMAIN),
            };
            // Config loaded from config.json
            return runtimeConfig;
        } else {
            // config.json not available, falling back
        }
    } catch (e) {
        // config.json fetch failed, falling back
    }

    // Fall back to build-time env vars or defaults
    // Using build-time/default config
    runtimeConfig = {
        basename: '',
        defaultPath: 'manage/dashboard',
        fontFamily: `'OpenSans', sans-serif`,
        borderRadius: 12,
        API_SERVER: buildTimeConfig.API_SERVER || defaults.API_SERVER,
        BASE_PATH_BACKEND: buildTimeConfig.BASE_PATH_BACKEND || defaults.BASE_PATH_BACKEND,
        ENVIRONMENT_NAME: buildTimeConfig.ENVIRONMENT_NAME || defaults.ENVIRONMENT_NAME,
        KEY_GOOGLE_MAPS: buildTimeConfig.KEY_GOOGLE_MAPS || defaults.KEY_GOOGLE_MAPS,
        COGNITO_USER_POOL_ID: buildTimeConfig.COGNITO_USER_POOL_ID || defaults.COGNITO_USER_POOL_ID,
        COGNITO_APP_CLIENT_ID: buildTimeConfig.COGNITO_APP_CLIENT_ID || defaults.COGNITO_APP_CLIENT_ID,
        COGNITO_REGION: buildTimeConfig.COGNITO_REGION || defaults.COGNITO_REGION,
        COGNITO_DOMAIN: buildTimeConfig.COGNITO_DOMAIN || defaults.COGNITO_DOMAIN,
    };
    return runtimeConfig;
}

// Synchronous getter - returns current config or defaults if not yet initialized
// For backwards compatibility with existing imports
const config = {
    basename: '',
    defaultPath: 'manage/dashboard',
    fontFamily: `'OpenSans', sans-serif`,
    borderRadius: 12,
    get API_SERVER() {
        const value = getConfigValue(runtimeConfig?.API_SERVER, buildTimeConfig.API_SERVER, defaults.API_SERVER);
        if (!runtimeConfig) {
            console.warn('Config accessed before initConfig() completed');
        }
        return value;
    },
    get BASE_PATH_BACKEND() {
        return getConfigValue(runtimeConfig?.BASE_PATH_BACKEND, buildTimeConfig.BASE_PATH_BACKEND, defaults.BASE_PATH_BACKEND);
    },
    get ENVIRONMENT_NAME() {
        return getConfigValue(runtimeConfig?.ENVIRONMENT_NAME, buildTimeConfig.ENVIRONMENT_NAME, defaults.ENVIRONMENT_NAME);
    },
    get KEY_GOOGLE_MAPS() {
        return getConfigValue(runtimeConfig?.KEY_GOOGLE_MAPS, buildTimeConfig.KEY_GOOGLE_MAPS, defaults.KEY_GOOGLE_MAPS);
    },
    get COGNITO_USER_POOL_ID() {
        return getConfigValue(runtimeConfig?.COGNITO_USER_POOL_ID, buildTimeConfig.COGNITO_USER_POOL_ID, defaults.COGNITO_USER_POOL_ID);
    },
    get COGNITO_APP_CLIENT_ID() {
        return getConfigValue(runtimeConfig?.COGNITO_APP_CLIENT_ID, buildTimeConfig.COGNITO_APP_CLIENT_ID, defaults.COGNITO_APP_CLIENT_ID);
    },
    get COGNITO_REGION() {
        return getConfigValue(runtimeConfig?.COGNITO_REGION, buildTimeConfig.COGNITO_REGION, defaults.COGNITO_REGION);
    },
    get COGNITO_DOMAIN() {
        return getConfigValue(runtimeConfig?.COGNITO_DOMAIN, buildTimeConfig.COGNITO_DOMAIN, defaults.COGNITO_DOMAIN);
    },
};

export default config;
